export const externalLinksSchema = {
  type: "object",
  properties: {
    title: { type: "string" },
    url: { type: "string" },
    type: { type: "string", enum: ["external", "social"] },
    platform: { type: "string" },
  },
  required: ["title", "url"],
};

/** Free-form link with any http(s) target — also what `website` uses. */
export const EXTERNAL_URL_PATTERN = "^https?://.+$";

/**
 * The strict per-platform URL patterns — the single source of truth for what a
 * link of each platform may look like. The schema below references the same
 * constants, and clients (mobile, web) import them from here instead of
 * re-implementing the rules, so the client-produced URLs always satisfy the
 * server-grade validation.
 */
export const SOCIAL_PLATFORM_URL_PATTERNS = {
  email: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
  linkedin: "^https://(www\\.)?linkedin\\.com(/.*)?$",
  twitter: "^https://(www\\.)?(twitter|x)\\.com(/.*)?$",
  facebook: "^https://(www\\.)?facebook\\.com(/.*)?$",
  instagram: "^https://(www\\.)?instagram\\.com(/.*)?$",
  youtube: "^https://(www\\.)?youtube\\.com(/.*)?$",
  github: "^https://(www\\.)?github\\.com(/.*)?$",
  website: EXTERNAL_URL_PATTERN,
};

export const externalLinksArraySchema = {
  type: "object",
  properties: {
    type: { type: "string", enum: ["external", "social"] },
    title: { type: "string", minLength: 1 },
    platform: { type: ["string", "null"] },
    url: {
      type: "string",
      minLength: 1,
    },
  },
  required: ["type", "url"],
  allOf: [
    {
      if: {
        properties: { type: { const: "external" } },
        required: ["type"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: EXTERNAL_URL_PATTERN,
            errorMessage: {
              pattern:
                "URL is not a valid external link (must start with http:// or https://)",
            },
          },
        },
        required: ["title"],
      },
    },
    {
      if: {
        properties: { type: { const: "social" } },
        required: ["type"],
      },
      then: { required: ["platform"] },
    },
    // email
    {
      if: {
        properties: { platform: { type: "string", const: "email" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.email,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // linkedin
    {
      if: {
        properties: { platform: { type: "string", const: "linkedin" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.linkedin,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // twitter/x
    {
      if: {
        properties: { platform: { type: "string", const: "twitter" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.twitter,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // facebook
    {
      if: {
        properties: { platform: { type: "string", const: "facebook" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.facebook,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // instagram
    {
      if: {
        properties: { platform: { type: "string", const: "instagram" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.instagram,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // youtube
    {
      if: {
        properties: { platform: { type: "string", const: "youtube" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.youtube,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // github
    {
      if: {
        properties: { platform: { type: "string", const: "github" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.github,
            errorMessage: {
              pattern: "URL does not match the selected platform format",
            },
          },
        },
      },
    },
    // website
    {
      if: {
        properties: { platform: { type: "string", const: "website" } },
        required: ["platform"],
      },
      then: {
        properties: {
          url: {
            type: "string",
            pattern: SOCIAL_PLATFORM_URL_PATTERNS.website,
          },
          errorMessage: {
            pattern: "URL does not match the selected platform format",
          },
        },
      },
    },
  ],
};
